import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
    ArrowLeft, Bed, Bookmark, Car, Check, Compass, CreditCard, MapPin,
    Pencil, Plane, Plus, Trash2, X,
} from 'lucide-react';
import { Header } from '../components/Header';
import { clearGatewaySession } from '../services/api';
import { DestinationCard } from '../components/DestinationCard';
import { Slider } from '../components/Slider';
import DESTINATIONS from '../data/destinations';
import { useProfile } from '../context/ProfileContext';
import {
    AIRLINE_OPTIONS, AMENITY_OPTIONS, CURRENCY_OPTIONS, DIETARY_OPTIONS,
    INTEREST_OPTIONS, PROPERTY_TYPE_OPTIONS,
} from '../types/profile';
import type { BookingKind, CabinClass, SavedTrip, SeatPreference, TripPace } from '../types/profile';
import type { Destination } from '../types/destination';
import { PROFILE_TABS, profileTabFromSearch, type ProfileTabKey } from '../utilities/profileTabs';
import styles from './ProfilePage.module.scss';

type TabKey = ProfileTabKey;

const TABS = PROFILE_TABS;

const KIND_ICON: Record<BookingKind, React.ReactNode> = {
    flight: <Plane size={17} />,
    hotel: <Bed size={17} />,
    car: <Car size={17} />,
    activity: <Compass size={17} />,
};

/** Photo thumbnail when the booking has one; the kind glyph otherwise —
 * older bookings and ones mapped from a real order_confirmation may not
 * carry image data. */
const BookingThumb = ({ kind, image, title }: { kind: BookingKind; image?: string; title: string }) =>
    image ? (
        <img src={image} alt={title} className={styles.bookingThumb} />
    ) : (
        <span className={styles.bookingIcon}>{KIND_ICON[kind]}</span>
    );

const CABINS: { value: CabinClass; label: string }[] = [
    { value: 'economy', label: 'Economy' },
    { value: 'premium_economy', label: 'Premium economy' },
    { value: 'business', label: 'Business' },
    { value: 'first', label: 'First' },
];

const SEATS: { value: SeatPreference; label: string }[] = [
    { value: 'window', label: 'Window' },
    { value: 'aisle', label: 'Aisle' },
    { value: 'no_preference', label: 'No preference' },
];

const PACES: { value: TripPace; label: string; hint: string }[] = [
    { value: 'relaxed', label: 'Relaxed', hint: 'One thing a day' },
    { value: 'balanced', label: 'Balanced', hint: 'A couple of plans' },
    { value: 'packed', label: 'Packed', hint: 'See everything' },
];

const fmtMoney = (amount: number, currency: string) => {
    try {
        return new Intl.NumberFormat(undefined, {
            style: 'currency', currency, maximumFractionDigits: 0,
        }).format(amount);
    } catch {
        return `${currency} ${amount.toLocaleString()}`;
    }
};

const fmtDateRange = (start: string, end: string) => {
    const s = new Date(start);
    const e = new Date(end);
    if (Number.isNaN(s.getTime())) return '';
    const opts: Intl.DateTimeFormatOptions = { day: 'numeric', month: 'short' };
    const left = s.toLocaleDateString(undefined, opts);
    if (Number.isNaN(e.getTime())) return `${left}, ${s.getFullYear()}`;
    return `${left} – ${e.toLocaleDateString(undefined, opts)}, ${e.getFullYear()}`;
};

const relativeTime = (iso: string) => {
    const then = new Date(iso).getTime();
    if (Number.isNaN(then)) return '';
    const diff = Date.now() - then;
    const mins = Math.round(diff / 60000);
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins}m ago`;
    const hours = Math.round(mins / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.round(hours / 24);
    if (days < 30) return `${days}d ago`;
    return new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
};

/* ── Reusable controls ──────────────────────────────── */

const TagPicker = ({
    options, selected, onToggle, label, hint,
}: {
    options: string[];
    selected: string[];
    onToggle: (value: string) => void;
    label: string;
    hint?: string;
}) => (
    <fieldset className={styles.field}>
        <legend className={styles.fieldLabel}>{label}</legend>
        {hint && <p className={styles.fieldHint}>{hint}</p>}
        <div className={styles.tagRow}>
            {options.map(option => {
                const active = selected.includes(option);
                return (
                    <button
                        key={option}
                        type="button"
                        className={`${styles.toggleTag} ${active ? styles.toggleTagActive : ''}`}
                        onClick={() => onToggle(option)}
                        aria-pressed={active}
                    >
                        {active && <Check size={12} aria-hidden="true" />}
                        {option}
                    </button>
                );
            })}
        </div>
    </fieldset>
);

const SegmentedControl = <T extends string>({
    options, value, onChange, label,
}: {
    options: { value: T; label: string; hint?: string }[];
    value: T;
    onChange: (v: T) => void;
    label: string;
}) => (
    <fieldset className={styles.field}>
        <legend className={styles.fieldLabel}>{label}</legend>
        <div className={styles.segmented} role="radiogroup" aria-label={label}>
            {options.map(opt => (
                <button
                    key={opt.value}
                    type="button"
                    role="radio"
                    aria-checked={value === opt.value}
                    className={`${styles.segment} ${value === opt.value ? styles.segmentActive : ''}`}
                    onClick={() => onChange(opt.value)}
                >
                    {opt.label}
                    {opt.hint && <small>{opt.hint}</small>}
                </button>
            ))}
        </div>
    </fieldset>
);

const Toggle = ({
    checked, onChange, label, description,
}: {
    checked: boolean;
    onChange: (v: boolean) => void;
    label: string;
    description?: string;
}) => (
    <label className={styles.toggleRow}>
        <span>
            <span className={styles.toggleLabel}>{label}</span>
            {description && <span className={styles.toggleDesc}>{description}</span>}
        </span>
        <input
            type="checkbox"
            className={styles.toggleInput}
            checked={checked}
            onChange={e => onChange(e.target.checked)}
        />
        <span className={styles.toggleTrack} aria-hidden="true"><span className={styles.toggleThumb} /></span>
    </label>
);

/** The full DestinationCard data behind a saved trip: by slug when the trip
 * carries one, otherwise by name for trips saved before slugs were stored. */
const resolveSavedDestination = (trip: SavedTrip): Destination | undefined => {
    const all = DESTINATIONS as Destination[];
    return all.find(d => d.slug === trip.slug)
        ?? all.find(d => d.name.toLowerCase() === trip.destination.toLowerCase());
};

/** "Are you sure?" prompt for removing a saved item. */
const ConfirmRemoveDialog = ({ name, onCancel, onConfirm }: {
    name: string;
    onCancel: () => void;
    onConfirm: () => void;
}) => {
    useEffect(() => {
        const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onCancel(); };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [onCancel]);

    return createPortal(
        <div className={styles.confirmOverlay} onClick={onCancel}>
            <div
                className={styles.confirmDialog}
                role="alertdialog"
                aria-modal="true"
                aria-labelledby="confirm-remove-title"
                onClick={e => e.stopPropagation()}
            >
                <h3 id="confirm-remove-title" className={styles.confirmTitle}>
                    Are you sure you want to remove {name}?
                </h3>
                <p className={styles.confirmText}>It will be taken out of your saved destinations.</p>
                <div className={styles.confirmActions}>
                    <button className={styles.btnOutline} type="button" onClick={onCancel} autoFocus>Cancel</button>
                    <button className={styles.btnDanger} type="button" onClick={onConfirm}>Remove</button>
                </div>
            </div>
        </div>,
        document.getElementById('modal-root') ?? document.body,
    );
};

/* ── Page ───────────────────────────────────────────── */

export const ProfilePage = () => {
    const { user, isAuthenticated, isLoading, loginWithRedirect, logout } = useAuth0();
    const navigate = useNavigate();
    const {
        profile, profileLoading, profileSaving, profileError, updateProfile, updatePreferences, togglePreferenceItem,
        updateNotifications, removeBooking, removeSavedTrip, resetProfile,
    } = useProfile();

    const location = useLocation();
    const linkedTab = profileTabFromSearch(location.search);
    const [activeTab, setActiveTab] = useState<TabKey>(linkedTab ?? 'trips');
    const tabsRef = useRef<HTMLDivElement | null>(null);
    const [pendingRemove, setPendingRemove] = useState<SavedTrip | null>(null);
    const [editingIdentity, setEditingIdentity] = useState(false);
    const [draftName, setDraftName] = useState('');
    const [draftBio, setDraftBio] = useState('');
    const [draftCity, setDraftCity] = useState('');
    const [newDestination, setNewDestination] = useState('');

    /* The account menu links straight to a tab (`?tab=saved`). The header
       and stats fill the first screen, so the tab bar is brought into view
       too — otherwise the switch happens out of sight. Clicking a tab
       here only rewrites the URL, so it neither re-runs this nor scrolls. */
    useEffect(() => {
        if ((location.state as { tabClick?: boolean } | null)?.tabClick) return;
        setActiveTab(linkedTab ?? 'trips');
        if (linkedTab) tabsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        // Keyed on the navigation itself: the same link twice should still scroll.
    }, [location.key]);

    const selectTab = (tab: TabKey) => {
        setActiveTab(tab);
        navigate({ search: tab === 'trips' ? '' : `?tab=${tab}` }, { replace: true, state: { tabClick: true } });
    };

    const prefs = profile.preferences;

    const authName = (user?.name || user?.nickname || user?.email?.split('@')[0] || '') as string;
    const name = profile.displayName || authName || 'Guest traveller';
    const email = (user?.email || '') as string;
    const picture = user?.picture as string | undefined;
    const initials = name.trim().charAt(0).toUpperCase() || 'G';

    const { upcoming, past, spend } = useMemo(() => {
        const now = Date.now();
        const up = profile.bookings.filter(
            b => b.status !== 'cancelled' && new Date(b.endDate).getTime() >= now,
        );
        const pastTrips = profile.bookings.filter(
            b => b.status === 'cancelled' || new Date(b.endDate).getTime() < now,
        );
        const total = profile.bookings
            .filter(b => b.status !== 'cancelled')
            .reduce((sum, b) => sum + (Number.isFinite(b.amount) ? b.amount : 0), 0);
        return { upcoming: up, past: pastTrips, spend: total };
    }, [profile.bookings]);

    const countries = useMemo(
        () => new Set(profile.bookings.map(b => b.destination).filter(Boolean)).size,
        [profile.bookings],
    );

    const openIdentityEditor = () => {
        setDraftName(profile.displayName || authName);
        setDraftBio(profile.bio);
        setDraftCity(profile.homeCity);
        setEditingIdentity(true);
    };

    const saveIdentity = () => {
        updateProfile({
            displayName: draftName.trim(),
            bio: draftBio.trim(),
            homeCity: draftCity.trim(),
        });
        setEditingIdentity(false);
    };

    const addDestination = () => {
        const value = newDestination.trim();
        if (!value) return;
        if (!prefs.favoriteDestinations.includes(value)) {
            togglePreferenceItem('favoriteDestinations', value);
        }
        setNewDestination('');
    };

    if (isLoading || profileLoading) {
        return (
            <div className={styles.profilePage}>
                <Header />
                <div className={styles.content}><div className={styles.skeletonHero} /></div>
            </div>
        );
    }

    return (
        <div className={styles.profilePage}>
            <Header />
            <div className={styles.content}>
                <button className={styles.backBtn} onClick={() => navigate(-1)} type="button">
                    <ArrowLeft size={14} aria-hidden="true" /> Back
                </button>

                {/* ── Identity ── */}
                <section className={styles.hero}>
                    <div className={styles.avatar}>
                        {picture
                            ? <img src={picture} alt="" />
                            : <span className={styles.avatarFallback}>{initials}</span>}
                    </div>

                    {editingIdentity ? (
                        <div className={styles.heroInfo}>
                            <input
                                className={styles.inlineInput}
                                value={draftName}
                                onChange={e => setDraftName(e.target.value)}
                                placeholder="Display name"
                                aria-label="Display name"
                                autoFocus
                            />
                            <input
                                className={styles.inlineInputSm}
                                value={draftCity}
                                onChange={e => setDraftCity(e.target.value)}
                                placeholder="Home city"
                                aria-label="Home city"
                            />
                            <textarea
                                className={styles.inlineTextarea}
                                value={draftBio}
                                onChange={e => setDraftBio(e.target.value)}
                                placeholder="A line about how you travel"
                                aria-label="Bio"
                                rows={2}
                            />
                        </div>
                    ) : (
                        <div className={styles.heroInfo}>
                            <h1 className={styles.heroName}>{name}</h1>
                            {email && <p className={styles.heroEmail}>{email}</p>}
                            {profile.homeCity && (
                                <p className={styles.heroMeta}>
                                    <MapPin size={13} aria-hidden="true" /> {profile.homeCity}
                                </p>
                            )}
                            {profile.bio && <p className={styles.heroBio}>{profile.bio}</p>}
                            <span className={isAuthenticated ? styles.heroBadge : styles.heroBadgeGuest}>
                                {isAuthenticated ? 'Signed in' : 'Guest — preferences saved on this device only'}
                            </span>
                        </div>
                    )}

                    <div className={styles.heroActions}>
                        {editingIdentity ? (
                            <>
                                <button className={styles.btnPrimary} onClick={saveIdentity} type="button">
                                    <Check size={14} aria-hidden="true" /> Save
                                </button>
                                <button className={styles.btnOutline} onClick={() => setEditingIdentity(false)} type="button">
                                    <X size={14} aria-hidden="true" /> Cancel
                                </button>
                            </>
                        ) : (
                            <>
                                <button className={styles.btnOutline} onClick={openIdentityEditor} type="button">
                                    <Pencil size={14} aria-hidden="true" /> Edit profile
                                </button>
                                <button className={styles.btnPrimary} onClick={() => navigate('/chat')} type="button">
                                    Plan a trip
                                </button>
                            </>
                        )}
                        {profileSaving && <span className={styles.heroBadge}>Saving…</span>}
                        {!profileSaving && !profileError && isAuthenticated && (
                            <span className={styles.heroBadge}>Saved to account</span>
                        )}
                        {profileError && <span className={styles.heroBadgeGuest}>{profileError}</span>}
                    </div>
                </section>

                {/* ── Stats ── */}
                <div className={styles.stats}>
                    <div className={styles.statCard}>
                        <span className={styles.statValue}>{upcoming.length}</span>
                        <span className={styles.statLabel}>Upcoming</span>
                    </div>
                    <div className={styles.statCard}>
                        <span className={styles.statValue}>{past.length}</span>
                        <span className={styles.statLabel}>Completed</span>
                    </div>
                    <div className={styles.statCard}>
                        <span className={`${styles.statValue} ${styles.statValueWarm}`}>
                            {spend > 0 ? fmtMoney(spend, prefs.currency) : '—'}
                        </span>
                        <span className={styles.statLabel}>Total spent</span>
                    </div>
                    <div className={styles.statCard}>
                        <span className={styles.statValue}>{countries || '—'}</span>
                        <span className={styles.statLabel}>Destinations</span>
                    </div>
                </div>

                {/* ── Tabs ── */}
                <div className={styles.tabs} role="tablist" aria-label="Profile sections" ref={tabsRef}>
                    {TABS.map(tab => (
                        <button
                            key={tab.key}
                            role="tab"
                            aria-selected={activeTab === tab.key}
                            className={`${styles.tab} ${activeTab === tab.key ? styles.tabActive : ''}`}
                            onClick={() => selectTab(tab.key)}
                            type="button"
                        >
                            {tab.label}
                        </button>
                    ))}
                </div>

                {/* ── Trips ── */}
                {activeTab === 'trips' && (
                    <div className={styles.panel}>
                        {profile.bookings.length === 0 ? (
                            <div className={styles.empty}>
                                <Plane size={26} aria-hidden="true" />
                                <h3>No trips yet</h3>
                                <p>Bookings you make through Adiona will appear here with dates, prices and references.</p>
                                <button className={styles.btnPrimary} onClick={() => navigate('/chat')} type="button">
                                    Plan your first trip
                                </button>
                            </div>
                        ) : (
                            <>
                                {upcoming.length > 0 && (
                                    <>
                                        <h2 className={styles.sectionTitle}>Upcoming</h2>
                                        <ul className={styles.bookingList}>
                                            {upcoming.map(b => (
                                                <li key={b.id} className={styles.bookingCard}>
                                                    <BookingThumb kind={b.kind} image={b.image} title={b.title} />
                                                    <span className={styles.bookingInfo}>
                                                        <span className={styles.bookingTitle}>{b.title}</span>
                                                        <span className={styles.bookingMeta}>
                                                            {fmtDateRange(b.startDate, b.endDate)}
                                                            {b.reference && ` · ${b.reference}`}
                                                        </span>
                                                    </span>
                                                    <span className={styles.bookingPrice}>{fmtMoney(b.amount, b.currency)}</span>
                                                    <span className={`${styles.status} ${styles[`status_${b.status}`]}`}>{b.status}</span>
                                                    <button
                                                        className={styles.iconBtn}
                                                        onClick={() => removeBooking(b.id)}
                                                        aria-label={`Remove ${b.title}`}
                                                        type="button"
                                                    >
                                                        <Trash2 size={14} />
                                                    </button>
                                                </li>
                                            ))}
                                        </ul>
                                    </>
                                )}
                                {past.length > 0 && (
                                    <>
                                        <h2 className={styles.sectionTitle}>Past</h2>
                                        <ul className={styles.bookingList}>
                                            {past.map(b => (
                                                <li key={b.id} className={`${styles.bookingCard} ${styles.bookingCardPast}`}>
                                                    <BookingThumb kind={b.kind} image={b.image} title={b.title} />
                                                    <span className={styles.bookingInfo}>
                                                        <span className={styles.bookingTitle}>{b.title}</span>
                                                        <span className={styles.bookingMeta}>{fmtDateRange(b.startDate, b.endDate)}</span>
                                                    </span>
                                                    <span className={styles.bookingPrice}>{fmtMoney(b.amount, b.currency)}</span>
                                                    <span className={`${styles.status} ${styles[`status_${b.status}`]}`}>{b.status}</span>
                                                </li>
                                            ))}
                                        </ul>
                                    </>
                                )}
                            </>
                        )}

                        {profile.activity.length > 0 && (
                            <>
                                <h2 className={styles.sectionTitle}>Recent activity</h2>
                                <ul className={styles.activityList}>
                                    {profile.activity.slice(0, 8).map(item => (
                                        <li key={item.id} className={styles.activityItem}>
                                            <span className={styles.activityDot} aria-hidden="true" />
                                            <span className={styles.activityText}>{item.text}</span>
                                            <span className={styles.activityTime}>{relativeTime(item.at)}</span>
                                        </li>
                                    ))}
                                </ul>
                            </>
                        )}
                    </div>
                )}

                {/* ── Saved ── */}
                {activeTab === 'saved' && (
                    <div className={styles.panel}>
                        <h2 className={styles.sectionTitle}>Destinations</h2>
                        {profile.savedTrips.length === 0 ? (
                            <div className={styles.empty}>
                                <Bookmark size={26} aria-hidden="true" />
                                <h3>No saved destinations</h3>
                                <p>Save a destination while browsing and it will wait for you here.</p>
                                <button className={styles.btnPrimary} onClick={() => navigate('/')} type="button">
                                    Browse destinations
                                </button>
                            </div>
                        ) : (
                            <Slider draggable showArrows={false}>
                                {profile.savedTrips.map(trip => {
                                    const destination = resolveSavedDestination(trip);
                                    // A trip that no longer matches any destination card still
                                    // gets a plain tile, so it can be opened or removed.
                                    return destination ? (
                                        <DestinationCard
                                            key={trip.id}
                                            destination={destination}
                                            size="small"
                                            onRemove={() => setPendingRemove(trip)}
                                        />
                                    ) : (
                                        <div key={trip.id} className={styles.savedCard}>
                                            <div className={styles.savedBody}>
                                                <h3 className={styles.savedTitle}>{trip.title || trip.destination}</h3>
                                                <p className={styles.savedMeta}>
                                                    <MapPin size={12} aria-hidden="true" /> {trip.destination}
                                                </p>
                                                {trip.note && <p className={styles.savedNote}>{trip.note}</p>}
                                                <span className={styles.savedDate}>Saved {relativeTime(trip.savedAt)}</span>
                                            </div>
                                            <div className={styles.savedActions}>
                                                <button
                                                    className={styles.btnOutline}
                                                    type="button"
                                                    onClick={() => navigate('/chat', { state: { message: `Plan a trip to ${trip.destination}` } })}
                                                >
                                                    Plan this
                                                </button>
                                                <button
                                                    className={styles.iconBtn}
                                                    onClick={() => setPendingRemove(trip)}
                                                    aria-label={`Remove ${trip.destination}`}
                                                    type="button"
                                                >
                                                    <Trash2 size={14} />
                                                </button>
                                            </div>
                                        </div>
                                    );
                                })}
                            </Slider>
                        )}

                        <h2 className={styles.sectionTitle}>Hotels</h2>
                        <div className={styles.empty}>
                            <Bed size={26} aria-hidden="true" />
                            <h3>No saved hotels</h3>
                            <p>Hotels you save while searching will appear here.</p>
                        </div>
                    </div>
                )}

                {/* ── Preferences ── */}
                {activeTab === 'preferences' && (
                    <div className={styles.panel}>
                        <p className={styles.panelIntro}>
                            Adiona uses these to rank results. Everything saves as you change it.
                        </p>

                        <div className={styles.prefGroup}>
                            <h2 className={styles.sectionTitle}>Basics</h2>
                            <div className={styles.inlineFields}>
                                <label className={styles.field}>
                                    <span className={styles.fieldLabel}>Home airport</span>
                                    <input
                                        className={styles.input}
                                        value={prefs.homeAirport}
                                        onChange={e => updatePreferences({ homeAirport: e.target.value.toUpperCase().slice(0, 4) })}
                                        placeholder="e.g. LHR"
                                    />
                                </label>
                                <label className={styles.field}>
                                    <span className={styles.fieldLabel}>Currency</span>
                                    <select
                                        className={styles.input}
                                        value={prefs.currency}
                                        onChange={e => updatePreferences({ currency: e.target.value })}
                                    >
                                        {CURRENCY_OPTIONS.map(c => <option key={c} value={c}>{c}</option>)}
                                    </select>
                                </label>
                            </div>
                        </div>

                        <div className={styles.prefGroup}>
                            <h2 className={styles.sectionTitle}>Flights</h2>
                            <SegmentedControl label="Cabin" options={CABINS} value={prefs.cabin}
                                onChange={v => updatePreferences({ cabin: v })} />
                            <SegmentedControl label="Seat" options={SEATS} value={prefs.seat}
                                onChange={v => updatePreferences({ seat: v })} />
                            <SegmentedControl
                                label="Maximum stops"
                                options={[
                                    { value: '0' as const, label: 'Direct only' },
                                    { value: '1' as const, label: 'Up to 1 stop' },
                                    { value: '2' as const, label: 'Up to 2 stops' },
                                ]}
                                value={String(prefs.maxStops) as '0' | '1' | '2'}
                                onChange={v => updatePreferences({ maxStops: Number(v) as 0 | 1 | 2 })}
                            />
                            <Toggle
                                label="Prefer extra legroom"
                                description="Ranks seats with more pitch higher when the price gap is small"
                                checked={prefs.extraLegroom}
                                onChange={v => updatePreferences({ extraLegroom: v })}
                            />
                            <TagPicker
                                label="Preferred airlines"
                                hint="Ranked higher in results"
                                options={AIRLINE_OPTIONS}
                                selected={prefs.preferredAirlines}
                                onToggle={v => togglePreferenceItem('preferredAirlines', v)}
                            />
                            <TagPicker
                                label="Airlines to avoid"
                                hint="Filtered out unless nothing else fits"
                                options={AIRLINE_OPTIONS}
                                selected={prefs.avoidedAirlines}
                                onToggle={v => togglePreferenceItem('avoidedAirlines', v)}
                            />
                        </div>

                        <div className={styles.prefGroup}>
                            <h2 className={styles.sectionTitle}>Stays</h2>
                            <div className={styles.field}>
                                <span className={styles.fieldLabel}>
                                    Budget per night · {fmtMoney(prefs.budgetPerNightMin, prefs.currency)} – {fmtMoney(prefs.budgetPerNightMax, prefs.currency)}
                                </span>
                                <div className={styles.rangeRow}>
                                    <input
                                        type="range" min={0} max={1000} step={10}
                                        value={prefs.budgetPerNightMin}
                                        aria-label="Minimum price per night"
                                        onChange={e => updatePreferences({
                                            budgetPerNightMin: Math.min(Number(e.target.value), prefs.budgetPerNightMax - 10),
                                        })}
                                    />
                                    <input
                                        type="range" min={0} max={1000} step={10}
                                        value={prefs.budgetPerNightMax}
                                        aria-label="Maximum price per night"
                                        onChange={e => updatePreferences({
                                            budgetPerNightMax: Math.max(Number(e.target.value), prefs.budgetPerNightMin + 10),
                                        })}
                                    />
                                </div>
                            </div>
                            <div className={styles.field}>
                                <span className={styles.fieldLabel}>Minimum guest rating · {prefs.minHotelRating || 'any'}</span>
                                <input
                                    type="range" min={0} max={10} step={0.5}
                                    value={prefs.minHotelRating}
                                    aria-label="Minimum guest rating"
                                    onChange={e => updatePreferences({ minHotelRating: Number(e.target.value) })}
                                />
                            </div>
                            <TagPicker
                                label="Must-have amenities"
                                options={AMENITY_OPTIONS}
                                selected={prefs.hotelAmenities}
                                onToggle={v => togglePreferenceItem('hotelAmenities', v)}
                            />
                            <TagPicker
                                label="Property types"
                                options={PROPERTY_TYPE_OPTIONS}
                                selected={prefs.propertyTypes}
                                onToggle={v => togglePreferenceItem('propertyTypes', v)}
                            />
                        </div>

                        <div className={styles.prefGroup}>
                            <h2 className={styles.sectionTitle}>How you travel</h2>
                            <SegmentedControl label="Pace" options={PACES} value={prefs.pace}
                                onChange={v => updatePreferences({ pace: v })} />
                            <TagPicker
                                label="Interests"
                                options={INTEREST_OPTIONS}
                                selected={prefs.interests}
                                onToggle={v => togglePreferenceItem('interests', v)}
                            />
                            <Toggle
                                label="Usually travelling with children"
                                description="Prioritises family rooms, kids' clubs and step-free access"
                                checked={prefs.travellingWithKids}
                                onChange={v => updatePreferences({ travellingWithKids: v })}
                            />
                            <TagPicker
                                label="Dietary needs"
                                options={DIETARY_OPTIONS}
                                selected={prefs.dietaryNeeds}
                                onToggle={v => togglePreferenceItem('dietaryNeeds', v)}
                            />
                            <label className={styles.field}>
                                <span className={styles.fieldLabel}>Accessibility needs</span>
                                <textarea
                                    className={styles.textarea}
                                    value={prefs.accessibilityNeeds}
                                    onChange={e => updatePreferences({ accessibilityNeeds: e.target.value })}
                                    placeholder="Step-free access, wheelchair space, quiet room…"
                                    rows={2}
                                />
                            </label>

                            <fieldset className={styles.field}>
                                <legend className={styles.fieldLabel}>Favourite destinations</legend>
                                <div className={styles.tagRow}>
                                    {prefs.favoriteDestinations.map(dest => (
                                        <button
                                            key={dest}
                                            type="button"
                                            className={`${styles.toggleTag} ${styles.toggleTagActive}`}
                                            onClick={() => togglePreferenceItem('favoriteDestinations', dest)}
                                            aria-label={`Remove ${dest}`}
                                        >
                                            {dest} <X size={11} aria-hidden="true" />
                                        </button>
                                    ))}
                                </div>
                                <div className={styles.addRow}>
                                    <input
                                        className={styles.input}
                                        value={newDestination}
                                        onChange={e => setNewDestination(e.target.value)}
                                        onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addDestination(); } }}
                                        placeholder="Add a city or country"
                                        aria-label="Add a favourite destination"
                                    />
                                    <button className={styles.btnOutline} onClick={addDestination} type="button">
                                        <Plus size={14} aria-hidden="true" /> Add
                                    </button>
                                </div>
                            </fieldset>
                        </div>
                    </div>
                )}

                {/* ── Settings ── */}
                {activeTab === 'settings' && (
                    <div className={styles.panel}>
                        <div className={styles.prefGroup}>
                            <h2 className={styles.sectionTitle}>Notifications</h2>
                            <Toggle
                                label="Price drop alerts"
                                description="Tell me when a watched route or stay gets cheaper"
                                checked={profile.notifications.priceDropAlerts}
                                onChange={v => updateNotifications({ priceDropAlerts: v })}
                            />
                            <Toggle
                                label="Booking reminders"
                                description="Check-in windows, visa deadlines and departure times"
                                checked={profile.notifications.bookingReminders}
                                onChange={v => updateNotifications({ bookingReminders: v })}
                            />
                            <Toggle
                                label="Weekly inspiration"
                                description="A short digest of places matching your interests"
                                checked={profile.notifications.weeklyInspiration}
                                onChange={v => updateNotifications({ weeklyInspiration: v })}
                            />
                            <Toggle
                                label="Product updates"
                                description="Occasional notes about new Adiona features"
                                checked={profile.notifications.productUpdates}
                                onChange={v => updateNotifications({ productUpdates: v })}
                            />
                        </div>

                        <div className={styles.prefGroup}>
                            <h2 className={styles.sectionTitle}>Account</h2>
                            {isAuthenticated ? (
                                <div className={styles.accountRow}>
                                    <span>
                                        <span className={styles.toggleLabel}>Signed in as {email || name}</span>
                                        <span className={styles.toggleDesc}>Your bookings and preferences follow this account.</span>
                                    </span>
                                    <button
                                        className={styles.btnOutline}
                                        type="button"
                                        onClick={() => {
                                            clearGatewaySession();
                                            logout({ logoutParams: { returnTo: window.location.origin } });
                                        }}
                                    >
                                        Log out
                                    </button>
                                </div>
                            ) : (
                                <div className={styles.accountRow}>
                                    <span>
                                        <span className={styles.toggleLabel}>You are browsing as a guest</span>
                                        <span className={styles.toggleDesc}>Sign in to keep preferences across devices and book.</span>
                                    </span>
                                    <button className={styles.btnPrimary} type="button" onClick={() => loginWithRedirect()}>
                                        <CreditCard size={14} aria-hidden="true" /> Sign in
                                    </button>
                                </div>
                            )}
                        </div>

                        <div className={styles.prefGroup}>
                            <h2 className={styles.sectionTitle}>Data</h2>
                            <div className={styles.accountRow}>
                                <span>
                                    <span className={styles.toggleLabel}>Reset saved data</span>
                                    <span className={styles.toggleDesc}>
                                        Clears preferences, saved trips and activity stored on this device. Bookings are not cancelled.
                                    </span>
                                </span>
                                <button
                                    className={styles.btnDanger}
                                    type="button"
                                    onClick={() => {
                                        if (window.confirm('Reset all locally saved preferences and trips? This cannot be undone.')) {
                                            resetProfile();
                                        }
                                    }}
                                >
                                    <Trash2 size={14} aria-hidden="true" /> Reset
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {pendingRemove && (
                <ConfirmRemoveDialog
                    name={pendingRemove.destination}
                    onCancel={() => setPendingRemove(null)}
                    onConfirm={() => {
                        removeSavedTrip(pendingRemove.id);
                        setPendingRemove(null);
                    }}
                />
            )}
        </div>
    );
};
